import { useEffect } from "react"
import { BereichGate } from "@/components/bereich-gesperrt"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, LockKeyhole } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { productMedia } from "@/lib/product-media"
import { useProduktSperre, useTrainierbaresProdukt } from "@/lib/produkt-freigabe"
import { GeneralCoachChat } from "@/components/coach-chat-general"
import { PageHeader } from "@/components/page-header"
import { cn } from "@/lib/utils"
import { useCoach } from "@/lib/use-coach"
import { useSessionContext } from "@/lib/session"
import {
  COACH_FALLBACK_LABEL,
  TRAINING_PRODUKTE,
  type ProduktKey,
} from "@nl/shared"

const OPTION_BADGE_CLASS =
  "border-[var(--rule)] bg-muted/20 text-[11.5px] font-normal text-muted-foreground"

/**
 * Same tile style as the theme step of the Gesprächstraining, so the two
 * training entry points read as one family. Plain links rather than a radio
 * group: choosing a product navigates immediately, there is no second step.
 */
function ProduktKacheln() {
  const sperreVon = useProduktSperre()
  return (
    <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
      {productMedia.map((product) => {
        const sperre = product.available ? sperreVon(product.key) : undefined
        const startbar = product.available && !sperre
        const tile = (
          <>
            <img
              src={product.image}
              alt=""
              loading="lazy"
              className={cn(
                "aspect-[3/2] w-full object-cover",
                startbar
                  ? "transition-transform duration-300 group-hover:scale-[1.03]"
                  : "opacity-70 grayscale",
              )}
            />
            {/* Legibility gradient, same as the Gesprächstraining tiles: the
                caption sits on the photo, so the text is fixed to white in
                both themes. */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent px-4 pb-3.5 pt-20 [text-shadow:0_1px_2px_rgb(0_0_0/0.55)]">
              <div className="text-[15px] font-medium leading-snug text-white">
                {product.title}
              </div>
              <p className="mt-1 text-[13px] leading-relaxed text-white/90">
                {product.description}
              </p>
            </div>
            {/* Named rather than only greyed out: an unexplained grey card
                reads as broken, a labelled one as a roadmap. */}
            {!startbar && (
              <Badge className="absolute left-2 top-2 gap-1 border-0 bg-black/55 text-[11px] font-normal text-white">
                {sperre ? (
                  <>
                    <LockKeyhole className="h-3 w-3" aria-hidden />
                    {sperre}
                  </>
                ) : (
                  "In Vorbereitung"
                )}
              </Badge>
            )}
          </>
        )
        const tileClass =
          "group relative block overflow-hidden rounded-xl text-left outline-none ring-1 ring-foreground/10 transition-shadow focus-visible:ring-2 focus-visible:ring-ring/50"

        return startbar ? (
          <Link
            key={product.key}
            to="/produkttraining"
            search={{ produkt: product.key }}
            className={tileClass}
          >
            {tile}
          </Link>
        ) : (
          <button
            key={product.key}
            type="button"
            aria-disabled="true"
            className={cn(tileClass, "cursor-not-allowed")}
          >
            {tile}
          </button>
        )
      })}
    </div>
  )
}
function ProduktTrainingPage() {
  const { role } = useSessionContext()
  const navigate = useNavigate()
  const coach = useCoach()
  const coachName = coach?.name ?? COACH_FALLBACK_LABEL
  const { produkt: produktKey, gespraech } = Route.useSearch()

  // Same guard as the open chat: this is an advisor tool, other roles get their
  // dashboard.
  useEffect(() => {
    if (role === "coach") void navigate({ to: "/coach", replace: true })
    if (role === "manager") void navigate({ to: "/management", replace: true })
  }, [role, navigate])

  const produkt = useTrainierbaresProdukt(produktKey, gespraech)

  if (!produkt) {
    return (
      <div className="space-y-7">
        <PageHeader
          eyebrow="Produkttraining"
          title="Zu welchem Produkt möchtest du abgefragt werden?"
        />
        <section className="reveal space-y-4" aria-label="Produkt">
          <ProduktKacheln />
        </section>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-2 flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => void navigate({ to: "/produkttraining", search: {} })}
        >
          <ArrowLeft className="h-4 w-4" />
          Produkt wechseln
        </Button>
        <Badge variant="outline" className={OPTION_BADGE_CLASS}>
          {produkt.label}
        </Badge>
      </div>

      <div className="min-h-0 flex-1">
        <GeneralCoachChat
          key={produkt.key}
          threadId={gespraech}
          mode="training"
          // Deliberately without a trainingPhase: that would load the phase
          // playbook alongside, and the coach would quiz the need analysis
          // instead of the product. The product snippets are the question bank
          // here.
          produkt={produkt.key}
          onThreadCreated={(id) =>
            void navigate({
              to: "/produkttraining",
              search: { produkt: produkt.key, gespraech: id },
              replace: true,
            })
          }
          onReset={() =>
            void navigate({
              to: "/produkttraining",
              search: { produkt: produkt.key },
              replace: true,
            })
          }
          empty={{
            title: `${coachName} challenged dich zum Produkt ${produkt.label}.`,
            hint: "Such dir ein Thema aus oder schreib los. Ich challenge dich mit Fragen, und wo es hakt, erklären wir es zusammen, bis du es einem Kunden erklären kannst.",
            // Falls für ein Produkt noch keine Themen hinterlegt sind, bleibt
            // ein neutraler Einstieg, statt gar keinen Vorschlag zu zeigen.
            prompts: produkt.startPrompts.length
              ? produkt.startPrompts
              : ["Leg los."],
          }}
          placeholder="Deine Antwort …"
        />
      </div>
    </div>
  )
}

function ProduktTrainingPageGesperrt() {
  const { gespraech } = Route.useSearch()
  // An existing conversation stays reachable; the lock only stops a new one.
  return (
    <BereichGate bereich="produkttraining" offen={Boolean(gespraech)}>
      <ProduktTrainingPage />
    </BereichGate>
  )
}

export const Route = createFileRoute("/_app/produkttraining")({
  validateSearch: (
    search: Record<string, unknown>
  ): { produkt?: ProduktKey; gespraech?: string } => ({
    produkt: TRAINING_PRODUKTE.some((entry) => entry.key === search.produkt)
      ? (search.produkt as ProduktKey)
      : undefined,
    gespraech:
      typeof search.gespraech === "string" && search.gespraech.trim()
        ? search.gespraech.trim()
        : undefined,
  }),
  component: ProduktTrainingPageGesperrt,
})
